import { useMemo, useRef, useState } from 'react';
import { toProxiedWpUrl } from '@/utils/wpMediaUrl';
import { DetailHeroFragment, EventQuery, FeatureFragment } from "@graphqlTypes";
import { formatDate, getDateRange } from '@/core/utils';
import { Container, CursorFollower, Image, TypePill, HeadlineAnim, RhLink } from '@components';
import { getLocationLabel, getPricingCopy } from './detail-hero-utils';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';
import { usePostType } from '@/hooks/postType';

import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

import useParallax from '../Parallax/useParallax';

import styles from './DetailHero.module.scss';
import className from 'classnames/bind';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = className.bind(styles);

type DetailHeroProps = DetailHeroFragment & {
  page?: FeatureFragment['items'][number]['page'];
  headline: string;
  startDate?: string;
  heroLink?: string;
  publishDate?: string;
  cursorText?: string;
  isMembersOnly?: boolean;
  endDate?: string;
  pricingType?: EventQuery['rhEvent']['eventData']['pricingType'];
  price?: EventQuery['rhEvent']['eventData']['price'];
};

const DetailItem = ({ text, subtext }: { text: string; subtext?: string }) => {
  return (
    <div className={cx("detail-item")}>
      <p className="text-l">{text}</p>
      {subtext && <p className="text-s">{subtext}</p>}
    </div>
  );
}

export default function DetailHero(props: DetailHeroProps) {
  const {
     page,
     price,
     image,
     video,
     endDate,
     timeText,
     headline,
     location,
     heroLink,
     startDate,
     assetType,
     cursorText,
     pricingType,
     timeSubtext,
     publishDate,
     dateSubtext,
    isMembersOnly,
    videoThumbnail,
    locationSubtext,
    includeTimeInfo,
    accessibilitySubtext,
    accessibilityFriendly,
  } = props;
  const { labels } = usePageWrapperData();
  const { accessibilityFriendlyLabel, freeExhibit, freeExhibitSubtext } = labels || {};
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const hasCustomCursor = Boolean(cursorText && heroLink && mounted && isReady && isDesktop);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const isEvent = page?.node?.__typename === "Event";
  const isExhibit = page?.node?.__typename === "Exhibit";
  const type = usePostType(page);
  const date = isEvent ? getDateRange(startDate, endDate) : null;
  const locationLabel = location?.[0] ? getLocationLabel(location[0], labels) : null;
  const pricingCopy = isEvent && pricingType?.[0] ? getPricingCopy(labels, pricingType[0], price, isMembersOnly) : null;
  const pressPublishDate = publishDate ? formatDate(publishDate, "MMMM d, yyyy") : null;
  const isVideo = useMemo(() => assetType?.[0] === 'video' && video, [assetType, video]);

  // Element refs
  const rootEl = useRef<HTMLDivElement>(null);
  const overlayEl = useRef<HTMLDivElement>(null);
  const imageEl = useRef<HTMLDivElement>(null);
  const headlineEl = useRef<HTMLHeadingElement>(null);
  const detailsEl = useRef<HTMLDivElement>(null);
  const bottomEl = useRef<HTMLDivElement>(null);

  // Note about "image" elements:
  // - "..image-container" is the outer wrapper that sets the aspect ratio and clipping
  // - "..image-wrapper" is the inner wrapper that is used for scaling the image with GSAP
  // - "..image" is the actual image element

  // -------------------- Animation

  const timelineAnimIn = useRef<gsap.core.Timeline | null>(null);
  const [isHeadlineAnimGo, setIsHeadlineAnimGo] = useState(false);

  // Timeline initialization
  useGSAP(() => {

    // If timeline already exists, kill it to reset
    if (timelineAnimIn.current) timelineAnimIn.current.kill();

    // Create timeline!
    timelineAnimIn.current = gsap.timeline({ paused: true }).add('start');

    const heroTl = timelineAnimIn.current;

    const gDur = 0.8; // General duration for animations

    // ---------- Image
    if (imageEl.current) {
      heroTl.add('image-in');
      heroTl.fromTo(
        imageEl.current,
        {
          scale: 1.5,
        }, {
          scale: 1,
          duration: gDur*2,
          ease: 'power3.out',
        },
        'image-in'
      );
    }

    // ---------- Overlay
    if (overlayEl.current) {
      heroTl.fromTo(
        overlayEl.current,
        {
          opacity: 1
        }, {
          opacity: 0.6,
          duration: gDur*2,
          ease: 'power3.out',
        },
        'image-in+=0.1'
      );
    }

    // ---------- Headline
    if (headlineEl.current) {
      heroTl.add('headline-in', `'-=${gDur*1.5}'`);
      heroTl.fromTo(
        headlineEl.current,
        {
          opacity: 0,
          y: '2rem'
        }, {
          opacity: 1,
          y: 0,
          duration: gDur,
          ease: 'power3.out',
          onStart: () => {
            setIsHeadlineAnimGo(true);
          }
        },
        'headline-in'
      );
    }

    // ---------- Border
    if (bottomEl.current) {
      heroTl.add('border-in', `'-=${gDur}'`);
      heroTl.fromTo(
        bottomEl.current,
        {
          scaleY: 0,
          scaleX: 0
        }, {
          scaleY: 1,
          scaleX: 1,
          duration: gDur,
          ease: 'power3.out',
        },
        'headline-in'
      );
    }

    // ---------- Details
    if (detailsEl.current) {
      heroTl.add('details-in', `'-=${gDur*0.9}'`);
      heroTl.fromTo(
        detailsEl.current,
        {
          opacity: 0,
          y: '1rem'
        }, {
          opacity: 1,
          y: 0,
          duration: gDur/2,
          ease: 'power4.out',
        },
        'details-in'
      );
    }

    // -------------------- Timeline creation complete

    heroTl.play();

  }, [image, headline, location, locationSubtext]);

  // -------------------- Parallax

  useParallax({ containerRef: rootEl, start: 'top top', distancePx: 120, isEnabled: true });

  // -------------------- Render

  return (
    <section className={cx(["detail-hero"])} ref={rootEl}>
      <div className={cx("detail-hero__overlay")} ref={overlayEl} />
      <div className={cx("detail-hero__image-container")}>
        <div className={cx("detail-hero__image-wrapper")} ref={imageEl}>
          {isVideo ? (
            <video
              className={cx("detail-hero__video", "js-parallax")}
              src={video || ''}
              poster={toProxiedWpUrl(videoThumbnail?.node?.mediaItemUrl) || ''}
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <Image image={image} className={`${cx("detail-hero__image")} js-parallax`} />
          )}
        </div>
      </div>
      {hasCustomCursor && (
        <RhLink
          href={heroLink}
          target="_blank"
          aria-label={cursorText}
          className={cx('link')}
          ref={anchorRef}
        >
          {mounted && isReady && isDesktop && (
            <CursorFollower
              container={anchorRef}
              circleText={cursorText}
              isVisible={true}
              mouseActive={true}
            />
          )}
        </RhLink>
      )}
      <Container className={cx("detail-hero__container")}>
        <div className={cx("detail-hero__content")}>
          { type?.key && (
            <div className={cx("info")}>
              <TypePill type={type.key} copy={type.label} />
            </div>
          )}
          <div className={cx("headline-wrap")} ref={headlineEl}>
            <HeadlineAnim
              tag="h1"
              className={cx(["headline", "title-uc-xl"])}
              text={headline}
              isRevealEnabled={true}
              isInView={isHeadlineAnimGo}
            />
          </div>
          <div className={cx("details")} ref={detailsEl}>
            {pressPublishDate && (
              <DetailItem text={pressPublishDate} />
            )}
            {includeTimeInfo && (
              <DetailItem text={timeText} subtext={timeSubtext} />
            )}
            {isEvent && (
              <DetailItem text={date} subtext={dateSubtext} />
            )}
            {locationLabel && (
              <DetailItem text={locationLabel} subtext={locationSubtext} />
            )}
            {accessibilityFriendly && (
              <DetailItem text={accessibilityFriendlyLabel} subtext={accessibilitySubtext} />
            )}
            {isEvent && pricingCopy && (
              <DetailItem text={pricingCopy.main} subtext={pricingCopy.sub} />
            )}
            {isExhibit && (
              <DetailItem text={freeExhibit} subtext={freeExhibitSubtext} />
            )}
          </div>
          <div className={cx("detail-hero__content__bottom")} ref={bottomEl} />
        </div>
      </Container>
    </section>
  );

}
